import React, { useState, useEffect, useRef } from 'react';
import { 
  Mail,
  Users,
  TrendingUp,
  Calendar,
  Download,
  Upload,
  Send,
  Filter,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Plus,
  Edit2,
  Trash2,
  AlertCircle,
  Tag,
  BarChart
} from 'lucide-react';
import { api } from '../utils/api';
import Modal from '../components/Modal';
import { exportToCSV } from '../utils/export';

const MailingListPage = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [deletingSubscriber, setDeletingSubscriber] = useState(null);
  const [importingSubscribers, setImportingSubscribers] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const fileInputRef = useRef(null);

  // Form state for add/edit modal
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    tags: [],
    source: 'manual'
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadSubscribers();
  }, []);

  const loadSubscribers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.subscribers.getAll();
      setSubscribers(response.subscribers);
    } catch (error) {
      console.error('Error loading subscribers:', error);
      setError('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    const errors = {};
    
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    try {
      const response = await api.subscribers.create(formData);
      setSubscribers([...subscribers, response.subscriber]);
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('Error adding subscriber:', error);
      setFormErrors({ submit: error.message || 'Failed to add subscriber' });
    }
  };

  const handleUpdateSubscriber = async (e) => {
    e.preventDefault();
    try {
      const response = await api.subscribers.update(editingSubscriber.email, formData);
      setSubscribers(subscribers.map(s => 
        s.email === editingSubscriber.email ? response.subscriber : s
      ));
      setEditingSubscriber(null);
      resetForm();
    } catch (error) {
      console.error('Error updating subscriber:', error);
      setFormErrors({ submit: error.message || 'Failed to update subscriber' });
    }
  };

  const handleDeleteSubscriber = async () => {
    try {
      await api.subscribers.delete(deletingSubscriber.email);
      setSubscribers(subscribers.filter(s => s.email !== deletingSubscriber.email));
      setDeletingSubscriber(null);
    } catch (error) {
      console.error('Error deleting subscriber:', error);
      setError('Failed to delete subscriber');
    }
  };

  const handleImportSubscribers = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportingSubscribers(true);
    setError(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          const rows = text.split('\n');
          const headers = rows[0].split(',').map(h => h.trim().toLowerCase());
          
          let importCount = 0;
          
          for (let i = 1; i < rows.length; i++) {
            if (!rows[i].trim()) continue;
            
            const values = rows[i].split(',').map(v => v.trim());
            const subscriber = {};
            
            headers.forEach((header, index) => {
              if (header === 'tags') {
                subscriber.tags = values[index] ? values[index].split(';').map(t => t.trim()) : [];
              } else {
                subscriber[header] = values[index] || '';
              }
            });
            
            if (subscriber.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(subscriber.email)) {
              try {
                await api.subscribers.create({
                  email: subscriber.email,
                  name: subscriber.name || '',
                  tags: subscriber.tags || [],
                  source: 'import'
                });
                importCount++;
              } catch (error) {
                console.error(`Failed to import ${subscriber.email}:`, error);
              }
            }
          }
          
          await loadSubscribers();
          alert(`Successfully imported ${importCount} subscribers`);
        } catch (error) {
          console.error('Error parsing CSV:', error);
          setError('Failed to parse CSV file');
        }
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error('Error importing subscribers:', error);
      setError('Failed to import subscribers');
    } finally {
      setImportingSubscribers(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportSubscribers = () => {
    const exportData = filteredSubscribers.map(subscriber => ({
      Email: subscriber.email,
      Name: subscriber.name || '',
      Status: subscriber.status,
      Tags: subscriber.tags?.join('; ') || '',
      Source: subscriber.source || '',
      'Date Joined': subscriber.subscribedAt ? new Date(subscriber.subscribedAt).toLocaleDateString() : '',
      'GDPR Consent': 'Yes'
    }));
    
    exportToCSV(exportData, 'subscribers.csv');
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      tags: [],
      source: 'manual'
    });
    setFormErrors({});
  };

  const handleTagInput = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const tag = e.target.value.trim();
      if (!formData.tags.includes(tag)) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      }
      e.target.value = '';
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const filteredSubscribers = subscribers.filter(sub => {
    const matchesSearch = 
      sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || sub.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const stats = {
    total: subscribers.length,
    active: subscribers.filter(s => s.status === 'active').length,
    thisWeek: subscribers.filter(s => {
      const subDate = new Date(s.subscribedAt);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return subDate >= weekAgo;
    }).length,
    unsubscribed: subscribers.filter(s => s.status === 'unsubscribed').length,
    openRate: '68%', // This would come from campaign data
    clickRate: '24%'  // This would come from campaign data
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A574] mx-auto mb-4"></div>
          <p className="text-[#888888]">Loading subscribers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light mb-2">Mailing List</h1>
          <p className="text-[#888888]">Manage your email subscribers and campaigns</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary flex items-center gap-2"
            disabled={importingSubscribers}
          >
            <Upload size={20} />
            {importingSubscribers ? 'Importing...' : 'Import'}
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <Plus size={20} />
            Add Subscriber
          </button>
          <button 
            onClick={() => setShowCampaignModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Send size={20} />
            New Campaign
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} className="text-red-500" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="card-dark text-center py-4">
          <Users className="mx-auto mb-2 text-[#2D6B42]" size={24} />
          <p className="text-2xl font-light">{stats.total}</p>
          <p className="text-xs text-[#888888]">Total</p>
        </div>
        <div className="card-dark text-center py-4">
          <CheckCircle className="mx-auto mb-2 text-green-500" size={24} />
          <p className="text-2xl font-light">{stats.active}</p>
          <p className="text-xs text-[#888888]">Active</p>
        </div>
        <div className="card-dark text-center py-4">
          <TrendingUp className="mx-auto mb-2 text-[#D4A574]" size={24} />
          <p className="text-2xl font-light">+{stats.thisWeek}</p>
          <p className="text-xs text-[#888888]">This Week</p>
        </div>
        <div className="card-dark text-center py-4">
          <XCircle className="mx-auto mb-2 text-red-500" size={24} />
          <p className="text-2xl font-light">{stats.unsubscribed}</p>
          <p className="text-xs text-[#888888]">Unsubscribed</p>
        </div>
        <div className="card-dark text-center py-4">
          <Mail className="mx-auto mb-2 text-blue-500" size={24} />
          <p className="text-2xl font-light">{stats.openRate}</p>
          <p className="text-xs text-[#888888]">Open Rate</p>
        </div>
        <div className="card-dark text-center py-4">
          <Clock className="mx-auto mb-2 text-purple-500" size={24} />
          <p className="text-2xl font-light">{stats.clickRate}</p>
          <p className="text-xs text-[#888888]">Click Rate</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 text-[#888888]" size={20} />
          <input
            type="text"
            placeholder="Search subscribers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-dark w-full pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="text-[#888888]" size={20} />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input-dark"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="unsubscribed">Unsubscribed</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <button 
          onClick={handleExportSubscribers}
          className="btn-secondary flex items-center gap-2"
        >
          <Download size={20} />
          Export CSV
        </button>
      </div>

      {/* Subscribers Table */}
      <div className="card-dark overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table-dark">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Status</th>
                <th>Tags</th>
                <th>Source</th>
                <th>Date Joined</th>
                <th>GDPR</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscribers.map((subscriber) => (
                <tr key={subscriber.email}>
                  <td>{subscriber.email}</td>
                  <td>{subscriber.name || '-'}</td>
                  <td>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      subscriber.status === 'active' 
                        ? 'bg-green-500/20 text-green-500'
                        : subscriber.status === 'unsubscribed'
                        ? 'bg-red-500/20 text-red-500'
                        : 'bg-yellow-500/20 text-yellow-500'
                    }`}>
                      {subscriber.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-1 flex-wrap">
                      {subscriber.tags?.map((tag, index) => (
                        <span key={index} className="text-xs px-2 py-1 bg-[#0A0A0A] rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="capitalize">{subscriber.source || 'manual'}</td>
                  <td>{subscriber.subscribedAt ? new Date(subscriber.subscribedAt).toLocaleDateString() : '-'}</td>
                  <td>
                    <CheckCircle className="text-green-500" size={16} />
                  </td>
                  <td className="text-right">
                    <div className="flex gap-2 justify-end">
                      <button 
                        onClick={() => {
                          setFormData({
                            email: subscriber.email,
                            name: subscriber.name || '',
                            tags: subscriber.tags || [],
                            source: subscriber.source || 'manual'
                          });
                          setEditingSubscriber(subscriber);
                        }}
                        className="p-1 hover:bg-[#0A0A0A] rounded transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button 
                        onClick={() => setDeletingSubscriber(subscriber)}
                        className="p-1 hover:bg-[#0A0A0A] rounded transition-colors text-red-500"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredSubscribers.length === 0 && (
          <div className="text-center py-12">
            <Mail className="mx-auto text-[#333333] mb-4" size={48} />
            <p className="text-[#888888]">No subscribers found</p>
          </div>
        )}
      </div>

      {/* Recent Campaigns & Growth */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-dark">
          <h2 className="text-xl font-light mb-4">Recent Campaigns</h2>
          <div className="space-y-4">
            <div className="p-4 bg-[#0A0A0A] rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-medium">Launch Announcement</h3>
                <span className="text-xs text-[#888888]">Planned</span>
              </div>
              <p className="text-sm text-[#888888] mb-2">Welcome email for new subscribers</p>
              <div className="flex items-center gap-4 text-xs text-[#888888]">
                <span>Recipients: {stats.active}</span>
                <span>•</span>
                <span>Scheduled: July 1</span>
              </div>
            </div>
            
            <button 
              onClick={() => setShowCampaignModal(true)}
              className="btn-secondary w-full text-sm"
            >
              Create New Campaign
            </button>
          </div>
        </div>

        <div className="card-dark">
          <h2 className="text-xl font-light mb-4">List Growth</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#888888]">Last 7 days</span>
              <span className="text-sm font-medium text-green-500">+{stats.thisWeek} subscribers</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#888888]">Last 30 days</span>
              <span className="text-sm font-medium text-green-500">
                +{subscribers.filter(s => {
                  const subDate = new Date(s.subscribedAt);
                  const monthAgo = new Date();
                  monthAgo.setDate(monthAgo.getDate() - 30);
                  return subDate >= monthAgo;
                }).length} subscribers
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#888888]">Growth rate</span>
              <span className="text-sm font-medium">
                {stats.total > 0 ? `+${Math.round((stats.thisWeek / stats.total) * 100)}% weekly` : '0%'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#888888]">Churn rate</span>
              <span className="text-sm font-medium">
                {stats.total > 0 ? `${((stats.unsubscribed / stats.total) * 100).toFixed(1)}%` : '0%'}
              </span>
            </div>
            
            <button className="btn-secondary w-full text-sm flex items-center justify-center gap-2">
              <BarChart size={16} />
              View Detailed Analytics
            </button>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleImportSubscribers}
        className="hidden"
      />

      {/* Add/Edit Subscriber Modal */}
      <Modal
        isOpen={showAddModal || !!editingSubscriber}
        onClose={() => {
          setShowAddModal(false);
          setEditingSubscriber(null);
          resetForm();
        }}
        title={editingSubscriber ? 'Edit Subscriber' : 'Add Subscriber'}
        maxWidth="max-w-md"
      >
        <form onSubmit={editingSubscriber ? handleUpdateSubscriber : handleAddSubscriber} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={`input-dark w-full ${formErrors.email ? 'border-red-500' : ''}`}
              placeholder="subscriber@example.com"
              disabled={!!editingSubscriber}
            />
            {formErrors.email && <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input-dark w-full"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              <Tag size={16} className="inline mr-1" />
              Tags
            </label>
            <input
              type="text"
              onKeyDown={handleTagInput}
              className="input-dark w-full mb-2"
              placeholder="Type tag and press Enter"
            />
            <div className="flex flex-wrap gap-2">
              {formData.tags.map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-[#0A0A0A] rounded-full text-sm flex items-center gap-2"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="text-[#888888] hover:text-white"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Source</label>
            <select
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              className="input-dark w-full"
            >
              <option value="manual">Manual</option>
              <option value="website">Website</option>
              <option value="event">Event</option>
              <option value="import">Import</option>
              <option value="partner">Partner</option>
            </select>
          </div>

          {formErrors.submit && (
            <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg">
              <p className="text-red-500 text-sm">{formErrors.submit}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={() => {
                setShowAddModal(false);
                setEditingSubscriber(null);
                resetForm();
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingSubscriber ? 'Save Changes' : 'Add Subscriber'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingSubscriber}
        onClose={() => setDeletingSubscriber(null)}
        title="Delete Subscriber"
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <p>Are you sure you want to delete <strong>{deletingSubscriber?.email}</strong>?</p>
          <p className="text-sm text-[#888888]">This action cannot be undone.</p>
          
          <div className="flex gap-3 justify-end pt-4">
            <button
              onClick={() => setDeletingSubscriber(null)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteSubscriber}
              className="btn-primary bg-red-500 hover:bg-red-600"
            >
              Delete Subscriber
            </button>
          </div>
        </div>
      </Modal>

      {/* Campaign Modal (placeholder) */}
      <Modal
        isOpen={showCampaignModal}
        onClose={() => setShowCampaignModal(false)}
        title="Create Email Campaign"
        maxWidth="max-w-2xl"
      >
        <div className="text-center py-12">
          <Send className="mx-auto text-[#333333] mb-4" size={48} />
          <p className="text-[#888888] mb-4">Email campaign functionality coming soon!</p>
          <p className="text-sm text-[#888888]">
            You'll be able to create and send beautiful email campaigns to your subscribers.
          </p>
          <button
            onClick={() => setShowCampaignModal(false)}
            className="btn-secondary mt-6"
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default MailingListPage;